import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ActionsButton, Datatable } from 'ui';
import { ChipButton, LinkButton } from 'ui/Buttons';
import qs from 'qs';
import { eventFiltersTypes } from 'shared/constants/eventFiltersTypes';
import { titleByEventFilter } from 'shared/constants/titleByEventFilter';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { SelectedEntities } from 'shared/types/portfolios';
import { MUIDataTableMeta } from 'mui-datatables';
import { Event } from 'modules/events/types/eventsTypes';
import { EventsActionButton } from 'modules/events/components/EventsActionButton';
import clsx from 'clsx';
import { useSelectedTableRows, useTable, useQuery, usePortfolioModals } from 'shared/hooks';
import { useSnackbar } from 'shared/hooks/useSnackbar';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { useDeleteEvents, useFetchPortfolioEvents } from '../../GraphQL/portfolios/hooks';
import { makeEventsTableData } from '../utils';
import { PortfolioItemsProps, PortfolioAction, PortfolioType } from '../types';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.grey[50],
    '& thead > tr': {
      whiteSpace: 'pre',
    },
  },
  buttonLink: {
    cursor: 'pointer',
  },
  buttonActive: {
    backgroundColor: '#ff8900',
  },
  actionButton: {
    height: 45,
    marginTop: 10,
    textAlign: 'center',
  },
}));

type PortfolioEventsParams = {
  eventFilter?: eventFiltersTypes;
};

export const PortfolioEvents: FC<PortfolioItemsProps> = ({
  portfolioId,
  total = 0,
  portfolioData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    singleSelect,
    multiSelect,
    currentSelect,
    selectedAll,
    onSelectAll,
    onSelectedChange,
    selectSingle,
  } = useSelectedTableRows();
  const queryParams = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: '' });
  const [updating, setUpdating] = useState(false);
  const { error, success } = useSnackbar();
  const { createPortfolio, addToPortfolio } = usePortfolioModals();
  const [variant, setVariant] = useState<PortfolioAction>();
  const deleteEvents = useDeleteEvents();
  const { eventFilter } = queryParams as PortfolioEventsParams;

  const { isDedicated } = portfolioData;

  /* ******************************************************************** */
  /*                            Event handlers                            */
  /* ******************************************************************** */

  const deleteEvent = () => {
    setUpdating(true);
    deleteEvents({
      eventsIds: selectedAll ? [] : Object.values(currentSelect.itemIds),
      portfolioId,
      selectedAll,
    })
      .then(({ data }) => {
        const message =
          (data as unknown as { deletePortfolioItems: any }).deletePortfolioItems?.message ||
          t('Success');
        refreshList(true, refreshReasons.DELETED);
        success(message);
      })
      .catch(() => {
        error(t('Unable to delete entities.'));
      })
      .finally(() => setUpdating(false));
  };

  const copyEvent = () => {
    setVariant(selectedAll ? 'copy_all' : 'copy');
    addToPortfolio.toggle();
  };

  const moveEvent = () => {
    setVariant(selectedAll ? 'move_all' : 'move');
    addToPortfolio.toggle();
  };

  const onEventFilterChange = (category?: string) => {
    if (category !== eventFilter) {
      const categoryConverted = category;
      navigate({
        pathname: location.pathname,
        search: qs.stringify(
          { ...queryParams, eventFilter: categoryConverted },
          { addQueryPrefix: true },
        ),
      });
    }
  };

  const refreshList = async (refresh: boolean, reason: string) => {
    setUpdating(true);
    await refetch();
    setShouldRefresh({ value: refresh, reason });
    setUpdating(false);
  };

  /* ******************************************************************** */
  /*                         Custom body renders                          */
  /* ******************************************************************** */

  const tabButtons = (
    <Box display="flex" py={2} px={1} minHeight={32}>
      {[undefined, ...Object.values(eventFiltersTypes)].map((category) => {
        const isActive = category === eventFilter;
        return (
          <ChipButton
            key={category ?? 'All Events'}
            isActive={isActive}
            onClick={() => onEventFilterChange(category)}
          >
            {!category ? t('PORTFOLIO.ALL_EVENTS') : titleByEventFilter[category]}
          </ChipButton>
        );
      })}
    </Box>
  );

  const nameRender = (name: string, { rowData: [[, artifactId]] }: MUIDataTableMeta) => (
    <LinkButton to={`/events?productId=${artifactId}`} label={name?.toUpperCase()} />
  );

  const toTitleCase = (text: string) => {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const categoryRenderer = (category: string) => toTitleCase(category.replace('_', ' '));

  const actionRender = (_: unknown, { rowData: [[id]] }: MUIDataTableMeta) => (
    <ActionsButton onOpen={() => selectSingle(id)} actions={getActions(singleSelect)} />
  );

  const eventActionRender = (event: Event) => (
    <EventsActionButton
      event={event}
      className={clsx(classes.buttonActive, classes.actionButton)}
      onRegister={refetch}
    />
  );

  /* ******************************************************************** */
  /*                       Table columns definition                       */
  /* ******************************************************************** */

  const initialColumns = useCallback(
    () => [
      {
        name: 'id',
        options: {
          display: false,
        },
      },
      {
        name: 'title',
        label: t('PORTFOLIO_EVENTS.NAME'),
        options: {
          customBodyRender: nameRender,
        },
      },
      {
        name: 'date',
        label: t('PORTFOLIO_EVENTS.DATE'),
      },
      {
        name: 'category',
        label: t('PORTFOLIO_EVENTS.CATEGORY'),
        options: {
          customBodyRender: categoryRenderer,
        },
      },
      {
        name: 'region',
        label: t('PORTFOLIO_EVENTS.REGION'),
      },
      {
        name: 'industry',
        label: t('PORTFOLIO_EVENTS.INDUSTRY'),
      },
      {
        name: 'eventActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: eventActionRender,
        },
      },
      {
        name: 'moreActions',
        label: ' ',
        options: {
          sort: false,
          customBodyRender: actionRender,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [singleSelect, multiSelect, currentSelect],
  );

  /* ******************************************************************** */
  /*                             More actions                             */
  /* ******************************************************************** */

  const getActions = (items: SelectedEntities) => [
    {
      text: 'Copy to portfolio',
      handler: copyEvent,
      hidden: items.count === 0,
      disabled: portfolioData.portfolioType === PortfolioType.SAMPLE_PORTFOLIO,
    },
    {
      text: 'Move to portfolio',
      handler: moveEvent,
      hidden: items.count === 0,
      disabled: isDedicated,
    },
    { text: 'Delete', handler: deleteEvent, hidden: items.count === 0, disabled: isDedicated },
  ];

  /* ******************************************************************** */
  /*                              Table data                              */
  /* ******************************************************************** */

  const { columns, pagination, onSortChange, onTableChange } = useTable({
    initialColumns,
  } as any);

  const { data, loading, refetch } = useFetchPortfolioEvents(
    portfolioId,
    pagination.page * pagination.limit,
    pagination.limit,
    pagination.sort && [
      {
        column: pagination.sort.sortColumn,
        direction: pagination.sort.direction,
      },
    ],
    { category: eventFilter },
  );
  const { events } = data || { events: [] };

  const tableData = useMemo(() => makeEventsTableData(events || []), [events]);

  useEffect(() => {
    refreshList(true, '');
  }, [eventFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ******************************************************************** */
  /*                                Render                                */
  /* ******************************************************************** */
  return (
    <Box className={classes.root}>
      <Datatable
        loading={loading || updating}
        columns={columns}
        pagination={pagination}
        data={tableData}
        total={total}
        onSelectedChange={onSelectedChange}
        toolbarData={getActions(multiSelect)}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        selectedAll={selectedAll}
        onSelectAllClick={onSelectAll}
        shouldRefresh={shouldRefresh}
        removeShadow
        leftToolbar={tabButtons}
        showSelectionExtension
      />
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        onActionSuccess={refreshList}
        itemsData={currentSelect}
        sourcePortfolioId={portfolioId}
        variant={variant}
        itemType="EVENT"
      />
    </Box>
  );
};
